import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../lib/api';

const NINETY_MIN_MS = 90 * 60 * 1000;

/** Persist schema version — bump when groupTimers shape changes. */
const SESSION_STORE_PERSIST_VERSION = 2;

/**
 * v1 cleanup: drop paused timers from persisted state. A paused discharge is
 * not a valid continuous 90-minute record — forcing a restart is the honest
 * outcome. Runs for anyone upgrading from v0.
 */
function migrateGroupTimersV1(groupTimers) {
  if (!groupTimers || typeof groupTimers !== 'object') return {};
  const next = {};
  for (const [groupName, timer] of Object.entries(groupTimers)) {
    if (!timer?.startedAt) continue;
    if (timer.pausedRemainingMs != null) continue;
    next[groupName] = {
      startedAt: timer.startedAt,
      durationMs: timer.durationMs || NINETY_MIN_MS,
    };
  }
  return next;
}

/**
 * v2 upgrade: add sync fields to any timer carried over from v1. Tag with the
 * persisted session's siteId so the reader can scope timers per site. Existing
 * (pre-sync) timers are treated as unsynced 'local' so reconnect re-POSTs them.
 */
function migrateGroupTimersV2(groupTimers, siteId) {
  if (!groupTimers || typeof groupTimers !== 'object') return {};
  const next = {};
  for (const [groupName, timer] of Object.entries(groupTimers)) {
    if (!timer?.startedAt) continue;
    next[groupName] = {
      startedAt: timer.startedAt,
      durationMs: timer.durationMs || NINETY_MIN_MS,
      syncState: timer.syncState || 'local',
      startedByName: timer.startedByName ?? null,
      siteId: timer.siteId != null ? String(timer.siteId) : (siteId != null ? String(siteId) : null),
    };
  }
  return next;
}

// Fire-and-forget POST /start for a timer. Never throws to the caller — a
// failed sync must never disturb the running local timer.
function postTimerStart({ siteId, groupName, sessionId, startedAtMs, durationMs }) {
  return api.post('/session-timers/start', {
    siteId: parseInt(siteId, 10),
    groupName,
    sessionId: sessionId != null && String(sessionId).trim() !== '' ? String(sessionId).trim() : undefined,
    startedAt: new Date(startedAtMs).toISOString(),
    durationMs,
  });
}

export const useSessionStore = create(
  persist(
    (set, get) => ({
      // ─── Hydration gate ─────────────────────────────────────────────────────
      // Mirrors authStore pattern. Guards against acting on default-null state
      // before localStorage has been loaded by the persist middleware.
      _hasHydrated: false,
      setHasHydrated: (val) => set({ _hasHydrated: val }),
      // ────────────────────────────────────────────────────────────────────────

      sessionId: null,
      /** ID of the site being tested (for session record on server). */
      siteId: null,
      /** Work order / job number for the current session (pre-flight). */
      workOrderNumber: '',
      /** Tracks if we've synced the session to the backend: 'idle' | 'pending' | 'created' | 'ended'. */
      serverSessionStatus: 'idle',
      // Results: { [assetId]: { status: 'pass'|'fail', testId, synced: boolean, timestamp } }
      results: {},
      // Queue: Array of assetIds waiting to sync to backend
      queue: [],
      // Derived count used for sync-status UX and reliability checks.
      pendingSyncCount: 0,
      // Parallel secondary-test queue. `results`/`queue` hold exactly ONE pending
      // result per asset (the "primary" test, e.g. an RCD's Operating Time), so a
      // SECOND test for the SAME asset in one visit (e.g. an RCD's Push Button) needs
      // its own queue — staging it into `results` would overwrite the primary result.
      // Each entry carries its own testType; same shape as results/queue; both are
      // flattened into a single /tests/batch call and synced together.
      secondaryTestQueue: {},
      secondaryTestQueueIds: [],
      // RCD Inputs: { [assetId]: { pushButton, tripTimeMs } } (Temporary UI state)
      rcdInputs: {},
      // Dual-check inputs: { [assetId]: { visual, operational } } — the two
      // PassFailToggle values for ceiling_fan / wall_heater / automated_gate
      // (Temporary UI state; each stays null until the tech explicitly answers it).
      dualCheckInputs: {},
      /** After leaveSession(), we keep this so initSession can preserve results when resuming the same session. */
      lastSessionId: null,

      /** Element type for the current session: 'electrical' | 'fitments'. Used for asset type filter and filtering. */
      sessionElementType: null,

      /** 'inspection' | 'repair' — sent with POST /sessions; repair requires work order. */
      sessionType: 'inspection',

      setSessionElementType: (elementType) =>
        set({ sessionElementType: elementType != null ? String(elementType).trim() : null }),

      // ─── 90-minute discharge timers ─────────────────────────────────────────
      // Persisted so timers survive tab switches, navigation, and page refreshes.
      // Shape: { [groupName]: { startedAt, durationMs, syncState, startedByName?, siteId } }
      // No pause — AS/NZS 2293.2 discharge is continuous (Start / Reset only).
      // Offline-first: local state is set immediately; the server is the source
      // of truth for SYNC, never a prerequisite for STARTING.
      groupTimers: {},
      /** Ends that failed to reach the server; drained on reconnect. */
      pendingTimerEnds: [],

      // Set local state immediately, then fire POST /start. On success adopt the
      // server's started_at (may be earlier — another device got there first)
      // and mark 'synced'. On failure keep the running local timer as 'local'.
      startGroupTimer: (groupName) => {
        const { siteId, sessionId } = get();
        const startedAtMs = Date.now();
        set((state) => ({
          groupTimers: {
            ...state.groupTimers,
            [groupName]: {
              startedAt: startedAtMs,
              durationMs: NINETY_MIN_MS,
              syncState: 'local',
              startedByName: null,
              siteId: siteId != null ? String(siteId) : null,
            },
          },
        }));
        if (siteId == null) return;
        postTimerStart({ siteId, groupName, sessionId, startedAtMs, durationMs: NINETY_MIN_MS })
          .then(({ data }) => {
            const srv = data?.timer;
            if (!srv) return;
            set((state) => {
              const existing = state.groupTimers[groupName];
              // Never resurrect a timer the user reset while the POST was in flight.
              if (!existing || String(existing.siteId) !== String(siteId)) return state;
              const serverStartedMs = Date.parse(srv.started_at);
              return {
                groupTimers: {
                  ...state.groupTimers,
                  [groupName]: {
                    ...existing,
                    startedAt: Number.isFinite(serverStartedMs) ? serverStartedMs : existing.startedAt,
                    durationMs: srv.duration_ms || existing.durationMs,
                    syncState: 'synced',
                    startedByName: srv.started_by_name || existing.startedByName || null,
                  },
                },
              };
            });
          })
          .catch(() => { /* keep the running local timer; reconnect re-POSTs */ });
      },
      restartGroupTimer: (groupName) => get().startGroupTimer(groupName),

      // Remove locally, fire POST /end. On failure queue the end for reconnect.
      resetGroupTimer: (groupName) => {
        const state = get();
        const timer = state.groupTimers[groupName];
        const endSiteId = timer?.siteId ?? state.siteId;
        set((s) => {
          if (!s.groupTimers[groupName]) return s;
          const next = { ...s.groupTimers };
          delete next[groupName];
          return { groupTimers: next };
        });
        if (endSiteId == null) return;
        api.post('/session-timers/end', { siteId: parseInt(endSiteId, 10), groupName })
          .then(() => {
            set((s) => ({
              pendingTimerEnds: s.pendingTimerEnds.filter(
                (e) => !(String(e.siteId) === String(endSiteId) && e.groupName === groupName)
              ),
            }));
          })
          .catch(() => {
            set((s) => {
              const already = s.pendingTimerEnds.some(
                (e) => String(e.siteId) === String(endSiteId) && e.groupName === groupName
              );
              return already
                ? s
                : { pendingTimerEnds: [...s.pendingTimerEnds, { siteId: String(endSiteId), groupName }] };
            });
          });
      },
      clearGroupTimers: () => set({ groupTimers: {}, pendingTimerEnds: [] }),

      /**
       * Merge server truth into local timers for the CURRENT site only.
       * Rules (see PR C brief): adopt server-only timers; keep the earlier
       * startedAt on live/live; drop a synced local when the server dropped it;
       * KEEP an unsynced local the server hasn't seen (started offline).
       */
      hydrateTimersFromServer: (siteId, serverTimers) =>
        set((state) => {
          const sid = siteId != null ? String(siteId) : null;
          if (sid == null || String(state.siteId) !== sid) return state; // scope to current site
          const serverByGroup = new Map();
          for (const t of serverTimers || []) serverByGroup.set(t.group_name, t);
          const next = { ...state.groupTimers };

          for (const [groupName, timer] of Object.entries(state.groupTimers)) {
            if (String(timer.siteId) !== sid) continue; // leave other sites untouched
            const srv = serverByGroup.get(groupName);
            if (srv) {
              const serverStartedMs = Date.parse(srv.started_at);
              const earlier = Number.isFinite(serverStartedMs)
                ? Math.min(timer.startedAt, serverStartedMs)
                : timer.startedAt;
              next[groupName] = {
                ...timer,
                startedAt: earlier,
                durationMs: timer.durationMs || srv.duration_ms || NINETY_MIN_MS,
                syncState: 'synced',
                startedByName: srv.started_by_name || timer.startedByName || null,
                siteId: sid,
              };
            } else if (timer.syncState === 'synced') {
              delete next[groupName]; // reset by another device
            }
            // else: unsynced local the server hasn't seen — KEEP (offline start)
          }

          for (const srv of serverTimers || []) {
            const existing = next[srv.group_name];
            if (existing && String(existing.siteId) === sid) continue;
            // Group names (building codes like "BOOK") repeat across sites. An
            // unsynced 'local' entry for a DIFFERENT site was started offline and
            // exists nowhere else — overwriting it would lose that discharge
            // permanently, and resyncLocalTimers would never re-POST it.
            if (existing && String(existing.siteId) !== sid && existing.syncState === 'local') continue;
            const serverStartedMs = Date.parse(srv.started_at);
            next[srv.group_name] = {
              startedAt: Number.isFinite(serverStartedMs) ? serverStartedMs : Date.now(),
              durationMs: srv.duration_ms || NINETY_MIN_MS,
              syncState: 'synced',
              startedByName: srv.started_by_name || null,
              siteId: sid,
            };
          }

          return { groupTimers: next };
        }),

      /** Re-POST any unsynced 'local' timers for the current site (reconnect). */
      resyncLocalTimers: () => {
        const { groupTimers, siteId, sessionId } = get();
        if (siteId == null) return;
        for (const [groupName, timer] of Object.entries(groupTimers)) {
          if (String(timer.siteId) !== String(siteId)) continue;
          if (timer.syncState !== 'local') continue;
          postTimerStart({ siteId, groupName, sessionId, startedAtMs: timer.startedAt, durationMs: timer.durationMs })
            .then(({ data }) => {
              const srv = data?.timer;
              if (!srv) return;
              set((state) => {
                const existing = state.groupTimers[groupName];
                if (!existing || String(existing.siteId) !== String(siteId)) return state;
                const serverStartedMs = Date.parse(srv.started_at);
                return {
                  groupTimers: {
                    ...state.groupTimers,
                    [groupName]: {
                      ...existing,
                      startedAt: Number.isFinite(serverStartedMs) ? serverStartedMs : existing.startedAt,
                      syncState: 'synced',
                      startedByName: srv.started_by_name || existing.startedByName || null,
                    },
                  },
                };
              });
            })
            .catch(() => { /* still offline; try again next reconnect */ });
        }
      },

      /** Drain queued ends (reconnect). Successful ends leave the queue. */
      drainPendingTimerEnds: () => {
        const { pendingTimerEnds } = get();
        for (const end of pendingTimerEnds) {
          api.post('/session-timers/end', { siteId: parseInt(end.siteId, 10), groupName: end.groupName })
            .then(() => {
              set((s) => ({
                pendingTimerEnds: s.pendingTimerEnds.filter(
                  (e) => !(String(e.siteId) === String(end.siteId) && e.groupName === end.groupName)
                ),
              }));
            })
            .catch(() => { /* still offline; retry next reconnect */ });
        }
      },
      // ────────────────────────────────────────────────────────────────────────

      // --- Actions ---

      /** Start or switch to a session. Resume (existingSessionId) forces status 'created' so we skip POST /sessions. */
      initSession: (siteId, existingSessionId = null, options = {}) => {
        const current = get();
        const resolvedSiteId = siteId != null ? String(siteId) : null;
        const existingId = existingSessionId != null && existingSessionId !== '' ? String(existingSessionId).trim() : null;
        // Guard: if a session is already active and no explicit resume ID is provided,
        // do not generate a new identity. This prevents asset-type switches (or any
        // other UI action) from silently creating a duplicate server session.
        if (!existingId && current.serverSessionStatus !== 'idle') {
          return;
        }

        const targetStatus = existingId ? 'created' : 'pending';
        const targetSessionId = existingId || (typeof crypto?.randomUUID === 'function' ? crypto.randomUUID() : `sess-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`);

        if (
          current.sessionId === targetSessionId &&
          current.serverSessionStatus === targetStatus
        ) {
          return;
        }

        const stOpt =
          options.sessionType != null && String(options.sessionType).trim() !== ''
            ? String(options.sessionType).trim().toLowerCase()
            : null;
        const nextSessionType =
          stOpt ?? (existingId ? current.sessionType || 'inspection' : 'inspection');

        set({
          siteId: resolvedSiteId,
          sessionId: targetSessionId,
          serverSessionStatus: targetStatus,
          lastSessionId: existingId || current.lastSessionId,
          workOrderNumber: current.workOrderNumber ?? '',
          sessionType: nextSessionType,
          ...(existingId ? {} : { results: {}, queue: [], pendingSyncCount: 0, secondaryTestQueue: {}, secondaryTestQueueIds: [] }),
        });
      },

      // --- 1. HARD RESET: Used ONLY when clicking "Finish Session" ---
      // This wipes everything clean for the next job.
      resetSession: () =>
        set({
          sessionId: null,
          siteId: null,
          workOrderNumber: '',
          serverSessionStatus: 'idle',
          lastSessionId: null,
          sessionElementType: null,
          sessionType: 'inspection',
          results: {},
          queue: [],
          pendingSyncCount: 0,
          secondaryTestQueue: {},
          secondaryTestQueueIds: [],
          rcdInputs: {},
          dualCheckInputs: {},
          groupTimers: {},
          pendingTimerEnds: [],
        }),

      // --- 2. SOFT LEAVE: Used when navigating away from the tests page ---
      // Puts down the session identity but keeps all data and timers in memory.
      leaveSession: () =>
        set((state) => ({
          sessionId: null,
          siteId: null,
          serverSessionStatus: 'idle',
          lastSessionId: state.sessionId,
          sessionElementType: state.sessionElementType,
          sessionType: state.sessionType,
          results: state.results,
          queue: state.queue,
          pendingSyncCount: state.queue.length,
          rcdInputs: state.rcdInputs,
          groupTimers: state.groupTimers,
        })),

      /** Call after POST /api/sessions succeeds. */
      markSessionCreated: () => set({ serverSessionStatus: 'created' }),

      // Optimistic Update: Save result locally and mark for sync
      stageResult: (assetId, resultData) => set((state) => {
        const existing = state.results[assetId];
        const testId = existing?.testId || null;
        const nextQueue = state.queue.includes(assetId) ? state.queue : [...state.queue, assetId];
        const tripTimeMs = resultData.metrics?.trip_time_ms;

        return {
          results: {
            ...state.results,
            [assetId]: {
              ...resultData,
              status: resultData.result,
              testId,
              synced: false,
              timestamp: Date.now(),
              ...(tripTimeMs != null ? { tripTimeMs: String(tripTimeMs) } : {}),
            }
          },
          queue: nextQueue,
          pendingSyncCount: nextQueue.length,
        };
      }),

      markSynced: (assetId, serverTestId) => set((state) => {
        const nextQueue = state.queue.filter((id) => id !== assetId);
        return {
          results: {
            ...state.results,
            [assetId]: { ...state.results[assetId], testId: serverTestId, synced: true }
          },
          queue: nextQueue,
          pendingSyncCount: nextQueue.length,
        };
      }),

      // Optimistic stage for the parallel secondary-test queue. Close copy of
      // stageResult, writing to secondaryTestQueue/secondaryTestQueueIds so it never
      // collides with the single-result-per-asset invariant of results/queue. The
      // eventData carries its own testType (e.g. 'Push Button', 'Operational Check').
      stageSecondaryTest: (assetId, eventData) => set((state) => {
        const existing = state.secondaryTestQueue[assetId];
        const testId = existing?.testId || null;
        const nextQueue = state.secondaryTestQueueIds.includes(assetId)
          ? state.secondaryTestQueueIds
          : [...state.secondaryTestQueueIds, assetId];

        return {
          secondaryTestQueue: {
            ...state.secondaryTestQueue,
            [assetId]: {
              ...eventData,
              status: eventData.result,
              testId,
              synced: false,
              timestamp: Date.now()
            }
          },
          secondaryTestQueueIds: nextQueue,
        };
      }),

      // Close copy of markSynced for the parallel secondary-test queue.
      markSecondaryTestSynced: (assetId, serverTestId) => set((state) => {
        const nextQueue = state.secondaryTestQueueIds.filter((id) => id !== assetId);
        return {
          secondaryTestQueue: {
            ...state.secondaryTestQueue,
            [assetId]: { ...state.secondaryTestQueue[assetId], testId: serverTestId, synced: true }
          },
          secondaryTestQueueIds: nextQueue,
        };
      }),

      setResultFromServer: (assetId, result, serverTestId, tripTimeMs) => set((state) => {
        const nextQueue = state.queue.filter((id) => id !== assetId);
        return {
          results: {
            ...state.results,
            [assetId]: {
              result, status: result, testId: serverTestId, synced: true, timestamp: Date.now(),
              ...(tripTimeMs != null ? { tripTimeMs: String(tripTimeMs) } : {}),
            }
          },
          queue: nextQueue,
          pendingSyncCount: nextQueue.length,
        };
      }),

      setRcdInput: (assetId, input) => set((state) => ({
        rcdInputs: {
          ...state.rcdInputs,
          [assetId]: { ...state.rcdInputs[assetId], ...input }
        }
      })),

      setDualCheckInput: (assetId, input) => set((state) => ({
        dualCheckInputs: {
          ...state.dualCheckInputs,
          [assetId]: { ...state.dualCheckInputs[assetId], ...input }
        }
      })),

      setWorkOrderNumber: (value) => set({ workOrderNumber: value != null ? String(value).trim() : '' }),
    }),
    {
      name: 'asset-session-storage',
      version: SESSION_STORE_PERSIST_VERSION,
      migrate: (persistedState, version) => {
        if (!persistedState || typeof persistedState !== 'object') return persistedState;
        let next = persistedState;
        // v0/absent → v1: drop paused timers (keep running for anyone on v0).
        if (!(version >= 1)) {
          next = { ...next, groupTimers: migrateGroupTimersV1(next.groupTimers) };
        }
        // →v2: add sync fields + siteId tag to carried timers.
        if (!(version >= 2)) {
          next = { ...next, groupTimers: migrateGroupTimersV2(next.groupTimers, next.siteId) };
        }
        return next;
      },
      partialize: (state) => ({
        sessionId: state.sessionId,
        siteId: state.siteId,
        workOrderNumber: state.workOrderNumber,
        serverSessionStatus: state.serverSessionStatus,
        sessionElementType: state.sessionElementType,
        sessionType: state.sessionType,
        results: state.results,
        queue: state.queue,
        secondaryTestQueue: state.secondaryTestQueue,
        secondaryTestQueueIds: state.secondaryTestQueueIds,
        rcdInputs: state.rcdInputs,
        dualCheckInputs: state.dualCheckInputs,
        groupTimers: state.groupTimers,
        pendingTimerEnds: state.pendingTimerEnds,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.pendingSyncCount = Array.isArray(state.queue) ? state.queue.length : 0;
        }
        state?.setHasHydrated(true);
      },
    }
  )
);
